import React, { useEffect, useState } from "react";
import {
  Center, 
  Flex,
  IconButton,
  Link,
  Box,
  Text 
} from "@chakra-ui/react";
import { differenceInDays, differenceInHours, differenceInMinutes, isBefore } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { NotificationBarType } from "../types/navbarTypes";
import { FiInfo } from "react-icons/fi";
import "./banner.scss";

interface BannerProps {
  onBannerStatusChange: (isBannerOpen: boolean) => void;
}

//  notification banner

const Banner: React.FC<BannerProps> = ({ onBannerStatusChange }) => {
  const [ isBannerOpen, setIsBannerOpen ] = useState( false );

  /* --- Load notification bar data --- */
  const DATA_ENDPOINT = "/data/activenotificationbar";
  const [notificationBarData, setNotificationBarData] = useState<NotificationBarType | null>( null );
  const [loadingNotificationData, setLoadingNotificationData] = useState<boolean>( true );
  const [error, setError] = useState<string | null>( null );

  useEffect(() => {
    const fetchNotificationBarData = async () => {
      try {
        const response = await fetch( DATA_ENDPOINT );

        if (!response.ok) {
          throw new Error(`Error: ${ response.statusText }`);
        }

        const json: any = await response.json();
        if (json && json.notificationBarOn) {
          setIsBannerOpen(true);
        }

        setNotificationBarData( json );
      }

      catch (err: any) {
        setError( err.message );
        console.error("Error fetching the notification bar data:", err.message)
      }

      finally {
        setLoadingNotificationData( false );
      }
    };

    fetchNotificationBarData();
  }, []);

  if (error) {
    console.log("Error in fetching notification bar data: ", error);
  }


  /* --- Report banner status to nav parent  --- */

  useEffect(() => {
    onBannerStatusChange( isBannerOpen );
  }, [ isBannerOpen, onBannerStatusChange ])


  /* --- FESTIVAL COUNTDOWN TIMER --- */

  const FESTIVAL_START_DATE = "2024-09-05 00:00:00";
  const FESTIVAL_START_DATE_ISO = "2024-09-05T00:00:00";
  const countdownTime = zonedTimeToUtc( FESTIVAL_START_DATE, "America/Toronto" );

  const getCountDownTimeObj = () => {
    const now = new Date();
    const remainingDays = differenceInDays( countdownTime, now );
    const remainingHours = differenceInHours( countdownTime, now ) % 24;
    const remainingMinutes = differenceInMinutes( countdownTime, now ) % 60;

    return {
      days: String( remainingDays ).padStart( 2, "0" ),
      hours: String( remainingHours ).padStart( 2, "0" ),
      mins: String( remainingMinutes ).padStart( 2, "0" ),
    };      
  }

  const [remainingTime, setRemainingTime] = useState(getCountDownTimeObj());

  // if countdown date has passed, don't display
  const dateCheck = new Date( FESTIVAL_START_DATE_ISO )

  useEffect(() => {
    const timer = setInterval(() =>{
      setRemainingTime(getCountDownTimeObj());
    }, 1000 * 60);
    return () => clearInterval( timer );
  }, [ countdownTime ]);

  // show festival countdown if before the festival date and no other notification banner
  const showFestivalCountdownBar = !isBefore(dateCheck, new Date()) && notificationBarData && !notificationBarData.notificationBarOn;

  // set banner to open
  if(showFestivalCountdownBar && isBannerOpen === false) setIsBannerOpen(true);

  return (
    <>
      {/* Festival countdown banner */}
      {showFestivalCountdownBar &&
        <Flex
          id="navbar-notification-bar"
          className="banner"
          direction="row"
          alignItems="center"
          h="40px"
          w="100%"
          bgColor="#1D1D1D"
          p="0 16px"
          marginBottom="-1px"
          marginTop="-1px"
          zIndex="1040"
          position="relative"
        >
          <Center w="100%">
            <Text 
              size={{ base: "countdown-bar-mobile", xl: "countdown-bar-desktop" }}
              fontSize={{ base: "12px !important", sm: "16px !important" }}
              lineHeight={{ base: "12px !important", sm: "16px !important" }}
              letterSpacing={{ base:"-0.18px !important", sm:"0.9px !important" }}
              fontFamily="Libre Franklin !important"
            >
              Countdown to #TIFF24 —
              <Box 
                as="span" 
                fontWeight="600" 
                fontFamily="Libre Franklin, sans-serif"
              >
                {" "}{ remainingTime.days } Days : { remainingTime.hours } Hrs : { remainingTime.mins } Mins
              </Box>
            </Text>
          </Center>
        </Flex>
      }

      {/* Year round banner */}
      {notificationBarData && notificationBarData.notificationBarOn &&
        <Flex
          id="navbar-notification-bar"
          className="banner"
          direction="row"
          alignItems="center"
          h="40px"
          w="100%"
          bgColor={ notificationBarData.backgroundColor ? `#${ notificationBarData.backgroundColor }` : "black" }
          p="0 16px"
          zIndex="1040"
          position="relative"
        >
          <Center width="100%">
            <Text
              size={{ base: "countdown-bar-mobile", xl: "countdown-bar-desktop" }}
              fontSize={{ base: "12px !important", sm: "16px !important" }}
              lineHeight={{ base: "12px !important", sm: "16px !important" }}
              letterSpacing={{ base:"-0.18px !important", sm:"0.9px !important" }}
              fontFamily="Libre Franklin !important"
              color={ notificationBarData.copyColor ? `#${ notificationBarData.copyColor } !important` : "white" }
              textAlign="center"
              margin="0 !important"
              padding="0 !important"
            >
              {notificationBarData.infoIconOn &&
                <IconButton
                  as="span"
                  className="banner-icon"
                  variant="primary.accent"
                  icon={ <FiInfo /> }
                  aria-label="Info alert"
                  height="fit-content"
                  minWidth={{ base: "24px", xl: "40px" }}
                />
              }
                { notificationBarData.copy }
            </Text>
          </Center>
        </Flex>
      }

    </>

  );
}

export default Banner;
